import { useLazyQuery } from '@apollo/client';
import * as Sentry from '@sentry/react';
import { Auth } from 'aws-amplify';
import PropTypes from 'prop-types';
import { useCallback, useEffect, useMemo, useReducer } from 'react';
import { useNavigate } from 'react-router';
import { useAlert } from 'src/components/alert';
import { useSnackbar } from 'src/components/snackbar';
import { useTenantConfig } from 'src/context/tenant';
import { GET_USER_BY_SUB } from 'src/graphql/users/queries';
import { useSearchParams } from 'src/routes/hook';
import { paths } from 'src/routes/paths';
import { COMMAND_CENTER_ORG_KEY } from 'src/utils/dashboard-org-header';
import { logError } from 'src/utils/error-logger';
import { isAppOnlyUser, isCrossOrgRole, webEligibleMemberships } from 'src/utils/roles';
import { AuthContext } from './auth-context';

const USER_ROLES = {
  SUPER_ADMIN: 'superadmin',
  MANAGER: 'manager',
  LEGISLATIVE_MEMBER: 'legislative_member',
  VOLUNTEER: 'volunteer',
  OWNER: 'owner' //same as manager, but owner is present as the role code in user_organizations
};

const initialState = {
  user: null,
  loading: true,
};

const reducer = (state, action) => {
  if (action.type === 'INITIAL') {
    return {
      loading: false,
      user: action.payload.user,
    };
  }
  if (action.type === 'VERIFY') {
    return {
      ...state,
      ...action.payload,
    };
  }
  if (action.type === 'LOGOUT') {
    return {
      ...state,
      user: null,
    };
  }
  return state;
};

export function AuthProvider({ children }) {
  const [state, dispatch] = useReducer(reducer, initialState);
  const [getUserBySub] = useLazyQuery(GET_USER_BY_SUB, {});
  const navigate = useNavigate();
  const searchParams = useSearchParams();
  const { enqueueSnackbar } = useSnackbar();
  const alert = useAlert();
  const { isLegislative: isLegislativeTenant } = useTenantConfig();

  const returnTo = searchParams.get('returnTo');

  useEffect(() => {
    if (state?.user?.email) {
      Sentry.setUser({
        email: state?.user?.email,
      });
    }
  }, [state?.user]);

  const initialize = useCallback(async (redirect = false) => {
    // Being signed out is the normal state on a public page, not a failure. Amplify rejects
    // here with a bare string, so letting it reach the outer catch fires an error snackbar
    // with an undefined message: a blank red toast on every anonymous visit.
    let currentUser;
    try {
      currentUser = await Auth.currentAuthenticatedUser();
    } catch {
      dispatch({ type: 'INITIAL', payload: { user: null } });
      return;
    }

    try {
      const { data, error } = await getUserBySub({
        variables: {
          sub: currentUser.attributes.sub,
        },
      });
      if (error) {
        throw new Error(error.message);
      }
      if (data.rows.length) {
        const user = data.rows[0];
        console.info('user', user);

        const userRole = user?.users_user_roles?.[0]?.user_roles_roles?.code;

        const isSuperAdmin = userRole === USER_ROLES.SUPER_ADMIN;
        const isCrossOrg = isCrossOrgRole(userRole);
        const isLegislativeMember = userRole === USER_ROLES.LEGISLATIVE_MEMBER;

        // With WEB_CALLING_ENABLED on, web is no longer a manager-only surface: a volunteer works
        // their assigned calling tasks here (see the Assigned Tasks spec), so access comes from
        // holding any active membership. With it off, only managed orgs count again — decided
        // by the per-org role, not the global one. Switched-off memberships (flg_inactive) grant
        // nothing either way.
        const eligibleOrgs = webEligibleMemberships(
          userRole,
          user?.users_user_organizations ?? []
        );
        // Someone who manages no org gets into the portal only as a calling volunteer: with
        // WEB_CALLING_ENABLED on and at least one of their volunteer orgs in
        // WEB_CALLING_ENABLED_ORG_IDS (isAppOnlyUser). Anyone else holding an active membership is
        // not refused: they are signed in and sent to the app-download page, where the mobile app
        // is their way in. Same rule as useIsAppOnlyUser, which keeps them there.
        const isAppOnly =
          !isLegislativeTenant && isAppOnlyUser(userRole, user?.users_user_organizations ?? []);
        const accessAllowed =
          isCrossOrg ||
          (isLegislativeTenant ? isLegislativeMember : eligibleOrgs.length > 0 || isAppOnly);

        if (!accessAllowed) {
          console.log("not authorized to access this application");
          logError(new Error("Not authorized to access this application"), {
            module: 'AuthProvider',
            customMessage: "Not authorized to access this application",
            extra: {
              userRole,
              isSuperAdmin,
              isLegislativeTenant,
              isLegislativeMember,
              eligibleOrgCount: eligibleOrgs.length,
              accessAllowed,
              user,
              currentUser,
              data,
              error,
            },
          });
          alert.dialog({
            severity: 'error',
            title: 'Not Authorized',
            description: 'You are not authorized to access this application. Please contact support if you believe this is an error.',
            confirmationText: 'OK',
            showCancelButton: false,
          });
          await logout();
          dispatch({
            type: 'INITIAL',
            payload: {
              user: null,
            },
          });
          return;
        }

        try {
          if (isCrossOrg) {
            localStorage?.removeItem?.('organization-context');
            localStorage?.removeItem?.(COMMAND_CENTER_ORG_KEY);
          }
        } catch (error) {
          console.error('Error removing organization context:', error);
        }

        // Initialize FreshCchat
        // window.fcWidget.show();
        // window.fcWidget.setConfig({
        //   externalId: `${user.user_id}-${user.email}`,
        // });
        // window.fcWidget.user.get(function (resp) {
        //   let status = resp && resp.status;
        //   if (status !== 200) {
        //     window.fcWidget.user.setProperties({
        //       firstName: user.first_name,
        //       lastName: user.last_name,
        //       email: user.email,
        //     });
        //   }
        // });
        // window.fcWidget.on('user:created', function (resp) {
        //   let status = resp && resp.status;
        //   let data = resp && resp.data;
        //   if (status === 200) {
        //     if (data?.restoreId) {
        //       // Update restoreId in your database
        //     }
        //   }
        // });

        dispatch({
          type: 'INITIAL',
          payload: {
            user: {
              ...currentUser,
              ...user,
              displayName: `${user.first_name} ${user.last_name}`,
            },
          },
        });
        if (redirect) {
          // Not PATH_AFTER_LOGIN directly: the organization the app will select is not known
          // yet, and the landing screen follows the role *there*, not in this user's first
          // membership. `/` (HomeRedirect in src/routes/sections/index.js) waits for it.
          // An app-only user goes straight to the download page, whatever returnTo says.
          navigate(isAppOnly ? paths.volunteers.root : returnTo || '/', { replace: true });
        }
      } else {
        dispatch({
          type: 'INITIAL',
          payload: {
            user: null,
          },
        });
      }
    } catch (error) {
      enqueueSnackbar(error.message, { variant: 'error' });
      dispatch({
        type: 'INITIAL',
        payload: {
          user: null,
        },
      });
    }
  }, []);

  useEffect(() => {
    initialize();
  }, [initialize]);

  // LOGIN
  const login = useCallback(async (email, password) => {
    const user = await Auth.signIn({
      username: email,
    });
    dispatch({
      type: 'VERIFY',
      payload: { user },
    });
  }, []);

  // verify
  const verify = useCallback(async (user, code) => {
    const resp = await Auth.sendCustomChallengeAnswer(user, code);
    if (resp.challengeName === 'CUSTOM_CHALLENGE') {
      dispatch({
        type: 'VERIFY',
        payload: {
          user: resp,
        },
      });
    }
    return resp;
  }, []);

  // REGISTER
  const register = useCallback(async (email, password, firstName, lastName) => {
    await Auth.signUp({
      username: email,
      password,
      attributes: {
        email,
        given_name: firstName,
        family_name: lastName,
      },
    });
  }, []);

  // CONFIRM REGISTER
  const confirmRegister = useCallback(async (email, code) => {
    await Auth.confirmSignUp(email, code);
  }, []);

  // RESEND CODE REGISTER
  const resendCodeRegister = useCallback(async (email) => {
    await Auth.resendSignUp(email);
  }, []);

  // LOGOUT
  const logout = useCallback(async () => {
    await Auth.signOut();
    // window.fcWidget.user.clear();
    // window.fcWidget.hide();
    localStorage?.removeItem('organization-context');
    localStorage?.removeItem(COMMAND_CENTER_ORG_KEY);
    Sentry.setUser(null);
    dispatch({
      type: 'LOGOUT',
    });
  }, []);

  // FORGOT PASSWORD
  const forgotPassword = useCallback(async (email) => {
    await Auth.forgotPassword(email);
  }, []);

  // NEW PASSWORD
  const newPassword = useCallback(async (email, code, password) => {
    await Auth.forgotPasswordSubmit(email, code, password);
  }, []);

  // ----------------------------------------------------------------------

  const checkAuthenticated = state.user ? 'authenticated' : 'unauthenticated';

  const status = state.loading ? 'loading' : checkAuthenticated;

  const memoizedValue = useMemo(
    () => ({
      user: state.user,
      method: 'amplify',
      loading: status === 'loading',
      authenticated: status === 'authenticated',
      unauthenticated: status === 'unauthenticated',
      //
      initialize,
      login,
      verify,
      logout,
      register,
      newPassword,
      forgotPassword,
      confirmRegister,
      resendCodeRegister,
    }),
    [
      status,
      state.user,
      initialize,
      login,
      verify,
      logout,
      register,
      newPassword,
      forgotPassword,
      confirmRegister,
      resendCodeRegister,
    ]
  );

  return <AuthContext.Provider value={memoizedValue}>{children}</AuthContext.Provider>;
}

AuthProvider.propTypes = {
  children: PropTypes.node,
};
